// SeoTable.js - Master Reusable Table Component
import React, { useEffect, useState } from "react";
import { Button, Card, Space, message, Modal } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { API_METHODS, BLANK_VALUE } from "../../constants";
import useApi from "../../hooks/useApi";
import { useNavigate } from "react-router-dom";
import TableComponent from "../../components/common/TableComponent";
import { SEO_CONFIG } from "./config/seoConfig";

const SeoTable = ({ entityType }) => {
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();

  // Get configuration for this entity type
  const config = SEO_CONFIG[entityType];

  // State
  const [tableData, setTableData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [confirmDeleteLoading, setConfirmDeleteLoading] = useState(false);

  // API hooks
  const [responseData, getRequest] = useApi(
    `${config.apiEndpoint}?pageNumber=1`,
    null,
    API_METHODS.GET
  );

  const [deleteResponse, deleteRequest] = useApi();

  // Load data on component mount
  useEffect(() => {
    getRequest();
  }, []);

  // Handle table data response
  useEffect(() => {
    const data = responseData.data !== BLANK_VALUE ? responseData.data : [];
    const pagination =
      responseData.pagination !== BLANK_VALUE ? responseData.pagination : {};
    setTableData(data);
    setTotalRecords(pagination ? pagination.count : 0);
  }, [responseData]);

  // Handle delete response
  useEffect(() => {
    if (deleteResponse.error && !deleteResponse.isLoading) {
      setConfirmDeleteLoading(false);
      setSelectedRecord(null);
      setShowDelete(false);
      messageApi.open({
        type: "error",
        content: `Error deleting ${config.title}`,
      });
    } else if (deleteResponse.data && !deleteResponse.isLoading) {
      getRequest(); // Refresh table
      setConfirmDeleteLoading(false);
      setSelectedRecord(null);
      setShowDelete(false);
      messageApi.open({
        type: "success",
        content: `${config.title} deleted successfully`,
      });
    }
  }, [deleteResponse]);

  // Define columns based on entity type
  const getColumns = () => {
    let baseColumns = [];

    // For static pages, show both page_identifier
    if (entityType === "static-page") {
      baseColumns = [
        {
          title: "Page Identifier",
          dataIndex: "page_identifier",
          key: "page_identifier",
        },
      ];
    } else {
      // For other entities, show entity name
      baseColumns = [
        {
          title: `${config.entityLabel} Name`,
          dataIndex: getEntityNameField(),
          key: "entity_name",
        },
      ];
    }

    // Add common columns
    baseColumns.push(
      {
        title: "Meta Title",
        dataIndex: "meta_title",
        key: "meta_title",
        ellipsis: true,
      },
      {
        title: "Actions",
        key: "actions",
        render: (text, record) => (
          <Space>
            <Button size="small" onClick={() => editRecord(record)}>
              <EditOutlined />
            </Button>
            <Button
              size="small"
              onClick={() => {
                setSelectedRecord(record);
                setShowDelete(true);
              }}
            >
              <DeleteOutlined style={{ color: "red" }} />
            </Button>
          </Space>
        ),
      }
    );

    return baseColumns;
  };

  // Get the correct field name for entity name based on entity type
  const getEntityNameField = () => {
    switch (entityType) {
      case "product":
        return "product_name";
      case "category":
        return "category";
      case "subcategory":
        return "subcategory";
      case "main-category":
        return "main_category";
      case "static-page":
        return "page_identifier";
      default:
        return "name";
    }
  };

  // Get the correct ID field for navigation
  const getEntityIdField = () => {
    return config.entityIdField;
  };

  // Edit record - navigate to edit form
  const editRecord = (record) => {
    const entityId = record[getEntityIdField()];
    navigate(`/portal/${entityType}-seo/edit/${entityId}`);
  };

  // Add new SEO record
  const addRecord = () => {
    navigate(`/portal/${entityType}-seo/add`);
  };

  // Delete confirmation
  const deleteOk = (record) => {
    setConfirmDeleteLoading(true);
    const entityId = record[getEntityIdField()];
    deleteRequest(
      null,
      API_METHODS.DELETE,
      `${config.apiEndpoint}/${entityId}`
    );
  };

  // Handle pagination change
  const onPaginationChange = (page) => {
    const url = `${config.apiEndpoint}?pageNumber=${page.current}`;
    getRequest(null, API_METHODS.GET, url);
    setCurrentPage(page.current);
  };

  return (
    <Card
      title={`${config.title} Management`}
      extra={
        <Button type="primary" onClick={addRecord}>
          Add {config.title}
        </Button>
      }
    >
      {contextHolder}

      <TableComponent
        columns={getColumns()}
        tableData={tableData}
        loading={responseData.isLoading}
        onPaginationChange={onPaginationChange}
        total={totalRecords}
        current={currentPage}
      />

      {/* Delete Confirmation Modal */}
      {showDelete && (
        <Modal
          title={`Delete ${config.title}`}
          open={showDelete}
          onOk={() => deleteOk(selectedRecord)}
          okText="Yes"
          okButtonProps={{
            loading: confirmDeleteLoading,
            danger: true,
          }}
          onCancel={() => setShowDelete(false)}
        >
          Are you sure you want to delete{" "}
          <b>{selectedRecord?.[getEntityNameField()]}</b> SEO data?
        </Modal>
      )}
    </Card>
  );
};

export default SeoTable;
